import { ErrorOutline } from '@mui/icons-material';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import React, { memo, useCallback, useEffect, useState } from 'react';
import SingleInput from './SingleInput';

export interface OTPInputProps {
  length: number;
  onChangeOTP: (otp: string) => void;
  error: {
    error: boolean;
    errorMessage?: string;
  };
  autoFocus?: boolean;
  disabled?: boolean;
  autoFillValue?: string;
}

export const OTPInputComponent = (props: OTPInputProps) => {
  const {
    length,
    autoFocus,
    disabled,
    onChangeOTP,
    error,
    autoFillValue,
    ...rest
  } = props;

  const [activeInput, setActiveInput] = useState(0);
  const [otpValues, setOTPValues] = useState(Array<string>(length).fill(''));

  const theme = useTheme();

  // Helper to return OTP from inputs
  const handleOtpChange = useCallback(
    (otp: string[]) => {
      const otpValue = otp.join('');
      onChangeOTP(otpValue);
    },
    [onChangeOTP],
  );

  // Change OTP value at focussing input
  const changeCodeAtFocus = useCallback(
    (str: string) => {
      const updatedOTPValues = [...otpValues];
      updatedOTPValues[activeInput] = str[0] || '';
      setOTPValues(updatedOTPValues);
      handleOtpChange(updatedOTPValues);
    },
    [activeInput, handleOtpChange, otpValues],
  );

  // Focus `inputIndex` input
  const focusInput = useCallback(
    (inputIndex: number) => {
      const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0);
      setActiveInput(selectedIndex);
    },
    [length],
  );

  const focusPrevInput = useCallback(() => {
    focusInput(activeInput - 1);
  }, [activeInput, focusInput]);

  const focusNextInput = useCallback(() => {
    focusInput(activeInput + 1);
  }, [activeInput, focusInput]);

  // Handle onFocus input
  const handleOnFocus = useCallback(
    (index: number) => () => {
      focusInput(index);
    },
    [focusInput],
  );

  // Handle onChange value for each input
  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.currentTarget.value;
      if (!val) {
        e.preventDefault();
        return;
      }
      changeCodeAtFocus(val);
      focusNextInput();
    },
    [changeCodeAtFocus, focusNextInput],
  );

  // Handle onBlur input
  const onBlur = useCallback(() => {
    setActiveInput(-1);
  }, []);

  // Handle onKeyDown input
  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const pressedKey = e.key;

      switch (pressedKey) {
        case 'Backspace':
        case 'Delete': {
          e.preventDefault();
          changeCodeAtFocus('');
          focusPrevInput();
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          focusPrevInput();
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          focusNextInput();
          break;
        }
        default: {
          if (pressedKey.match(/^[^a-zA-Z0-9]$/)) {
            e.preventDefault();
            focusNextInput();
          }

          break;
        }
      }
    },
    [changeCodeAtFocus, focusNextInput, focusPrevInput],
  );

  const handleOnPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedData = e.clipboardData
        .getData('text/plain')
        .trim()
        .slice(0, length - activeInput)
        .split('');
      if (pastedData) {
        let nextFocusIndex = 0;
        const updatedOTPValues = [...otpValues];
        updatedOTPValues.forEach((val, index) => {
          if (index >= activeInput) {
            const changedValue = pastedData.shift() || val;
            if (changedValue) {
              updatedOTPValues[index] = changedValue;
              nextFocusIndex = index;
            }
          }
        });
        setOTPValues(updatedOTPValues);
        setActiveInput(Math.min(nextFocusIndex + 1, length - 1));
        handleOtpChange(updatedOTPValues);
      }
    },
    [activeInput, length, otpValues, handleOtpChange],
  );

  useEffect(() => {
    if (autoFillValue) {
      const updatedOTPValues = autoFillValue
        .split('')
        .slice(0, length)
        .concat(otpValues.slice(autoFillValue.length));

      setOTPValues(updatedOTPValues);
      handleOtpChange(updatedOTPValues);
    }
  }, [autoFillValue, length, otpValues, handleOtpChange]);

  return (
    <Box {...rest}>
      {Array(length)
        .fill('')
        .map((_, index) => (
          <SingleInput
            key={`SingleInput-${index}`}
            type={'text'}
            focus={activeInput === index}
            value={otpValues?.[index]}
            autoFocus={autoFocus}
            onFocus={handleOnFocus(index)}
            onChange={handleOnChange}
            onKeyDown={handleOnKeyDown}
            onBlur={onBlur}
            onPaste={handleOnPaste}
            disabled={disabled || otpValues[index - 1] === ''}
            error={error.error}
          />
        ))}

      {error.error && (
        <Stack direction={'row'} spacing={1} alignItems={'center'} mt={1}>
          <ErrorOutline color={'error'} />
          <Typography color={theme.palette.error.main}>
            {error.errorMessage}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

const OTPInput = memo(OTPInputComponent);
export default OTPInput;
